/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { knowledgeBaseService, type KbListParams } from '../../services/knowledge-base.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { ProjectCell } from '../../components/common/project-cell';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import type { KnowledgeBase } from '../../types';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn, buildDefaultFilters, buildColumnFiltersParam } from '../../components/common/saas-data-table';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import { StatusChip } from '../../components/common/panel';
import { formatBytes, sourceKindLabel, statusTone } from './knowledge-base-status';

type KbRow = KnowledgeBase & { id: string };

const FILTER_DEFS: SaasColumn[] = [
  { field: 'kbCode', header: 'Code', filter: true },
  { field: 'name', header: 'Name', filter: true },
  { field: 'sourceKind', header: 'Made of', filter: true },
  { field: 'status', header: 'Status', filter: true },
];

/** 40×40 keeps every row action above the desktop hit-area floor. */
const rowAction = 'ui-press flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-gray-700';

const KnowledgeBaseList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({ entity: 'knowledge-base', basePath: '/knowledge-bases' });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.KNOWLEDGE_BASE) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.KNOWLEDGE_BASE);
  const canDelete = useCanDelete(JOB_NAMES.KNOWLEDGE_BASE);

  const [rows, setRows] = useState<KbRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showCore, setShowCore] = useState(false);
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => { projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([])); }, []);

  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));
  const debouncedSearchText = useDebounce(searchText, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 10000 });
  }, []);

  const fetchRows = useCallback(
    async (page = 1, size = pageSize, sortBy = sortField, order = sortOrder, activeFilters = filters) => {
      try {
        setLoading(true);
        const params: KbListParams = { page, page_size: size, include_deleted: false };
        if (debouncedSearchText?.trim()) params.search = debouncedSearchText.trim();
        if (sortBy) { params.sort_by = sortBy; params.sort_order = order === 1 ? 'asc' : 'desc'; }
        const filtersParam = buildColumnFiltersParam(activeFilters);
        if (filtersParam) params.filters = filtersParam;
        if (selectedProjectId) params.app_id = selectedProjectId;
        params.include_global = showCore;
        const response = await knowledgeBaseService.list(params);
        setRows((response.data || []).map((kb) => ({ ...kb, id: kb._id })));
        setTotalRecords((response as any).pagination?.totalItems ?? (response as any).pagination?.total ?? response.total ?? 0);
      } catch (error) {
        showError(extractErrorMessage(error, 'Failed to load knowledge bases'));
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearchText, selectedProjectId, showCore, pageSize, sortField, sortOrder, filters, showError]
  );

  const handlePageChange = (event: any) => { setFirst(event.first); setPageSize(event.rows); fetchRows(Math.floor(event.first / event.rows) + 1, event.rows, sortField, sortOrder); };
  const handleSort = (event: any) => { setSortField(event.sortField); setSortOrder(event.sortOrder); setFirst(0); fetchRows(1, pageSize, event.sortField, event.sortOrder); };
  const handleFilter = (event: any) => { const next = event.filters || {}; setFilters(next); setFirst(0); fetchRows(1, pageSize, sortField, sortOrder, next); };

  useEffect(() => { setFirst(0); fetchRows(1, pageSize); }, [debouncedSearchText, selectedProjectId, showCore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteConfirm = async () => {
    if (!selectedId) return;
    try {
      setDeleteLoading(true);
      await knowledgeBaseService.softDelete(selectedId);
      setDeleteDialogOpen(false); setSelectedId(null);
      fetchRows(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (error: any) {
      // The backend refuses a delete while a pipeline still reads the corpus, and
      // its message names them. Surfacing it verbatim is the whole point: the
      // operator's next action is to go and repoint those pipelines.
      setDeleteDialogOpen(false); setSelectedId(null);
      showError(extractErrorMessage(error, 'Failed to delete the knowledge base'));
    } finally {
      setDeleteLoading(false);
    }
  };

  // Code, Name, Documents, Size, Made of, Status, Project, Actions. Documents and
  // Size sit together because "is there anything in it" is the first question this
  // screen is opened to answer.
  const columns: SaasColumn<KbRow>[] = useMemo(() => {
    const cols: SaasColumn<KbRow>[] = [
      { field: 'kbCode', header: 'Code', sortable: true, filter: true,
        body: (r) => <span className="font-mono text-[13px]">{r.kbCode}</span> },
      { field: 'name', header: 'Name', sortable: true, filter: true,
        body: (r) => (
          <div className="min-w-0">
            <div className="truncate text-ink">{r.name}</div>
            {!!r.tags?.length && (
              <div className="mt-0.5 truncate text-[11px] text-ink-muted">
                {r.tags.join(' · ')}
              </div>
            )}
          </div>
        ) },
      { field: 'documentCount', header: 'Docs', sortable: true, align: 'right',
        body: (r) => <span className="tabular-nums">{r.documentCount ?? 0}</span> },
      { field: 'totalBytes', header: 'Size', align: 'right',
        body: (r) => <span className="tabular-nums text-ink-muted">{formatBytes(r.totalBytes)}</span> },
      { field: 'sourceKind', header: 'Made of', filter: true,
        body: (r) => (
          <StatusChip tone="info" dot={false}>{sourceKindLabel(r.sourceKind)}</StatusChip>
        ) },
      { field: 'status', header: 'Status', sortable: true, filter: true,
        body: (r) => <StatusChip tone={statusTone(r.status)}>{r.status === 'ACTIVE' ? 'Active' : 'Inactive'}</StatusChip> },
      { field: 'appId', header: 'Project', hideable: true, defaultVisible: canManageProjects,
        body: (r) => <ProjectCell appId={r.appId} projects={accessibleProjects} /> },
      { field: 'actions', header: 'Actions', align: 'center', hideable: false,
        body: (r) => (
          <div className="flex justify-center gap-0.5">
            <button type="button" className={rowAction} onClick={() => navigateToView(r._id)}
                    data-pr-tooltip="View" aria-label={`View ${r.name}`}>
              <i className="pi pi-eye ui-act--view text-[1.05rem]" aria-hidden />
            </button>
            {canUpdate && (
              <button type="button" className={rowAction} onClick={() => navigateToEdit(r._id)}
                      data-pr-tooltip="Edit" aria-label={`Edit ${r.name}`}>
                <i className="pi pi-pencil ui-act--edit text-[1.05rem]" aria-hidden />
              </button>
            )}
            {canDelete && (
              <button type="button" className={rowAction}
                      onClick={() => { setSelectedId(r._id); setDeleteDialogOpen(true); }}
                      data-pr-tooltip="Delete" aria-label={`Delete ${r.name}`}>
                <i className="pi pi-trash ui-act--delete text-[1.05rem]" aria-hidden />
              </button>
            )}
          </div>
        ) },
    ];
    return cols;
  }, [canUpdate, canDelete, accessibleProjects, canManageProjects, navigateToView, navigateToEdit]);  

  return (
    <PermissionGuard jobName={JOB_NAMES.KNOWLEDGE_BASE}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-KnowledgeBase-List">
        <PageHeader
          embedded={embedded}
          icon="pi pi-database"
          title="Knowledge Bases"
          subtitle="The corpora your pipelines index — documents, who owns them, and when they were last checked"
          testId="KnowledgeBase.Header"
        />
        <div className={embedded ? '' : 'ui-panel overflow-hidden'}>
          <SaasDataTable
            tableId="knowledge-base"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={rows}
            loading={loading}
            emptyMessage="No knowledge bases yet"
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search code, name, description or tags"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            onSort={handleSort}
            sortField={sortField ?? undefined}
            sortOrder={sortOrder ?? undefined}
            filters={filters}
            onFilter={handleFilter}
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canManageProjects && (
                  <Dropdown
                    value={selectedProjectId}
                    options={[{ label: 'All projects', value: null },
                              ...accessibleProjects.map((p) => ({ label: p.name, value: p._id }))]}
                    onChange={(e: any) => setSelectedProjectId(e.value)}
                    className="w-52"
                    aria-label="Filter by project"
                  />
                )}
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="KnowledgeBaseList.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={
              <>
                <span className="whitespace-nowrap text-sm tabular-nums text-ink-muted">
                  {loading && !rows.length ? 'loading…' : `${totalRecords} ${totalRecords === 1 ? 'record' : 'records'}`}
                </span>
                <RefreshButton onClick={() => fetchRows(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)} loading={loading} />
                {canCreate && (
                  <span className="ui-press inline-flex">
                    <Button label="Create Knowledge Base" icon="pi pi-plus" onClick={() => navigateToCreate()} size="small" />
                  </span>
                )}
              </>
            }
          />
        </div>
        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={() => { setDeleteDialogOpen(false); setSelectedId(null); }}
          onDelete={handleDeleteConfirm}
          loading={deleteLoading}
          header="Delete knowledge base"
          message="Its documents, and any index already built from them, are kept. A pipeline that still reads this corpus will block the delete and name itself."
          testId="KnowledgeBase.Dialog.Delete"
        />
      </div>
    </PermissionGuard>
  );
};

export default KnowledgeBaseList;
